/** the page's own load wins; slow third parties do not hold the task longer */
export const LOAD_WAIT_MAX_MS = 4000
/** an idle moment is waited for at most this long */
export const IDLE_WAIT_MAX_MS = 2000

/**
 * Runs `task` once, after the page has loaded and the main thread is free, so
 * code fetched ahead of a click never competes with the page's first paint.
 * Returns a cancel function.
 */
export const whenPageIdle = (task: () => void) => {
  let done = false
  let idleId: number | null = null
  const timers: ReturnType<typeof setTimeout>[] = []

  const run = () => {
    if (!done) {
      done = true
      task()
    }
  }
  const afterLoad = () => {
    window.removeEventListener('load', afterLoad)
    if (done || idleId !== null) {
      return
    }
    // Safari has no requestIdleCallback
    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(run, { timeout: IDLE_WAIT_MAX_MS })
    } else {
      timers.push(setTimeout(run, 0))
    }
  }

  if (document.readyState === 'complete') {
    afterLoad()
  } else {
    window.addEventListener('load', afterLoad)
    timers.push(setTimeout(afterLoad, LOAD_WAIT_MAX_MS))
  }

  return () => {
    done = true
    window.removeEventListener('load', afterLoad)
    timers.forEach(clearTimeout)
    if (idleId !== null && 'cancelIdleCallback' in window) {
      window.cancelIdleCallback(idleId)
    }
  }
}
